export type TimelineInterval = {
  start: number;
  end: number;
};

export type TimelineBreak = TimelineInterval & {
  durationMs: number;
  x0: number;
  x1: number;
};

type TimelineScaleSegment = TimelineInterval & {
  kind: "time" | "break";
  x0: number;
  x1: number;
};

export type BrokenTimelineScale = {
  domainStart: number;
  domainEnd: number;
  width: number;
  pixelsPerMillisecond: number;
  breaks: TimelineBreak[];
  toX: (timestamp: number) => number;
  fromX: (position: number) => number;
  breakAtX: (position: number) => TimelineBreak | null;
};

export const TIMELINE_BREAK_THRESHOLD_RATIO = 0.15;
export const TIMELINE_BREAK_THRESHOLD_ROUNDING_RATIO = 0.005;
export const TIMELINE_BREAK_MAX_TOTAL_RATIO = 0.2;
export const TIMELINE_BREAK_PREFERRED_WIDTH = 32;

const clamp = (value: number, minimum: number, maximum: number) =>
  Math.max(minimum, Math.min(maximum, value));

const mergeOccupiedIntervals = (
  intervals: TimelineInterval[],
  domainStart: number,
  domainEnd: number
): TimelineInterval[] => {
  const clipped = intervals
    .filter(({ start, end }) => Number.isFinite(start) && Number.isFinite(end) && end > start)
    .map(({ start, end }) => ({
      start: clamp(start, domainStart, domainEnd),
      end: clamp(end, domainStart, domainEnd)
    }))
    .filter(({ start, end }) => end > start)
    .sort((a, b) => a.start - b.start || a.end - b.end);

  const merged: TimelineInterval[] = [];
  for (const interval of clipped) {
    const previous = merged.at(-1);
    if (!previous || interval.start > previous.end) {
      merged.push({ ...interval });
      continue;
    }
    previous.end = Math.max(previous.end, interval.end);
  }
  return merged;
};

export const findTimelineBreaks = ({
  intervals,
  domainStart,
  domainEnd,
  thresholdRatio = TIMELINE_BREAK_THRESHOLD_RATIO
}: {
  intervals: TimelineInterval[];
  domainStart: number;
  domainEnd: number;
  thresholdRatio?: number;
}): TimelineInterval[] => {
  const duration = domainEnd - domainStart;
  if (!Number.isFinite(duration) || duration <= 0) return [];

  const occupied = mergeOccupiedIntervals(intervals, domainStart, domainEnd);
  // An entirely empty viewport should remain an ordinary empty state instead of
  // collapsing into one break with no temporal context on either side.
  if (!occupied.length) return [];

  const gaps: TimelineInterval[] = [];
  let cursor = domainStart;
  for (const interval of occupied) {
    if (interval.start > cursor) gaps.push({ start: cursor, end: interval.start });
    cursor = Math.max(cursor, interval.end);
  }
  if (cursor < domainEnd) gaps.push({ start: cursor, end: domainEnd });

  const normalizedThreshold = clamp(thresholdRatio, 0, 1);
  // The threshold is a display rule presented as a whole percentage. Match
  // that visual language by rounding to the nearest percentage point, so a
  // 14.67% gap is treated as the 15% gap a person sees instead of missing the
  // break because of sub-pixel/domain-boundary precision.
  const roundedThresholdFloor = Math.max(
    0,
    normalizedThreshold - TIMELINE_BREAK_THRESHOLD_ROUNDING_RATIO
  );
  return gaps.filter((gap) => (gap.end - gap.start) / duration >= roundedThresholdFloor);
};

export const createBrokenTimelineScale = ({
  intervals,
  domainStart,
  domainEnd,
  width,
  thresholdRatio = TIMELINE_BREAK_THRESHOLD_RATIO,
  maximumBreakRatio = TIMELINE_BREAK_MAX_TOTAL_RATIO,
  preferredBreakWidth = TIMELINE_BREAK_PREFERRED_WIDTH
}: {
  intervals: TimelineInterval[];
  domainStart: number;
  domainEnd: number;
  width: number;
  thresholdRatio?: number;
  maximumBreakRatio?: number;
  preferredBreakWidth?: number;
}): BrokenTimelineScale => {
  const safeStart = Number.isFinite(domainStart) ? domainStart : 0;
  const safeEnd = Number.isFinite(domainEnd) && domainEnd > safeStart ? domainEnd : safeStart + 1;
  const safeWidth = Math.max(1, Number.isFinite(width) ? width : 1);
  const candidates = findTimelineBreaks({
    intervals,
    domainStart: safeStart,
    domainEnd: safeEnd,
    thresholdRatio
  });
  const totalDuration = safeEnd - safeStart;
  const skippedDuration = candidates.reduce((sum, gap) => sum + gap.end - gap.start, 0);
  const normalDuration = totalDuration - skippedDuration;

  const breakBudget = safeWidth * clamp(maximumBreakRatio, 0, 1);
  const breakWidth = candidates.length
    ? Math.min(Math.max(0, preferredBreakWidth), breakBudget / candidates.length)
    : 0;

  // A valid occupied interval guarantees some normal duration, but retain a
  // linear fallback for defensive use with synthetic or malformed input.
  const usableCandidates = normalDuration > 0 && breakWidth > 0 ? candidates : [];
  const usableSkippedDuration = usableCandidates.reduce((sum, gap) => sum + gap.end - gap.start, 0);
  const usableNormalDuration = totalDuration - usableSkippedDuration;
  const remainingWidth = safeWidth - usableCandidates.length * breakWidth;
  const pixelsPerMillisecond = remainingWidth / usableNormalDuration;

  const segments: TimelineScaleSegment[] = [];
  let timeCursor = safeStart;
  let xCursor = 0;
  for (const gap of usableCandidates) {
    if (gap.start > timeCursor) {
      const x1 = xCursor + (gap.start - timeCursor) * pixelsPerMillisecond;
      segments.push({ kind: "time", start: timeCursor, end: gap.start, x0: xCursor, x1 });
      xCursor = x1;
    }
    segments.push({ kind: "break", start: gap.start, end: gap.end, x0: xCursor, x1: xCursor + breakWidth });
    xCursor += breakWidth;
    timeCursor = gap.end;
  }
  if (timeCursor < safeEnd || !segments.length) {
    segments.push({ kind: "time", start: timeCursor, end: safeEnd, x0: xCursor, x1: safeWidth });
  } else {
    const last = segments.at(-1);
    if (last) last.x1 = safeWidth;
  }

  const breaks = segments
    .filter((segment): segment is TimelineScaleSegment & { kind: "break" } => segment.kind === "break")
    .map((segment) => ({
      start: segment.start,
      end: segment.end,
      durationMs: segment.end - segment.start,
      x0: segment.x0,
      x1: segment.x1
    }));

  const toX = (timestamp: number): number => {
    const target = clamp(Number.isFinite(timestamp) ? timestamp : safeStart, safeStart, safeEnd);
    const segment = segments.find((entry) => target <= entry.end) ?? segments.at(-1)!;
    const ratio = (target - segment.start) / Math.max(1, segment.end - segment.start);
    return segment.x0 + clamp(ratio, 0, 1) * (segment.x1 - segment.x0);
  };

  const fromX = (position: number): number => {
    const target = clamp(Number.isFinite(position) ? position : 0, 0, safeWidth);
    const segment = segments.find((entry) => target <= entry.x1) ?? segments.at(-1)!;
    const ratio = (target - segment.x0) / Math.max(Number.EPSILON, segment.x1 - segment.x0);
    return segment.start + clamp(ratio, 0, 1) * (segment.end - segment.start);
  };

  const breakAtX = (position: number): TimelineBreak | null => {
    const target = clamp(Number.isFinite(position) ? position : 0, 0, safeWidth);
    return breaks.find((gap) => target >= gap.x0 && target <= gap.x1) ?? null;
  };

  return {
    domainStart: safeStart,
    domainEnd: safeEnd,
    width: safeWidth,
    pixelsPerMillisecond,
    breaks,
    toX,
    fromX,
    breakAtX
  };
};
